import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Locale } from '@/strings/types';

/**
 * Tab 4 — the Action Items page (/actions), and the scripted sequence that plays
 * on it.
 *
 * Structure and behaviour follow the real page (MoMP frontend/src/pages/Actions.tsx
 * and pages/actions/*): the four KPI cards are filter BUTTONS, the summary /
 * owner / priority / deadline cells are single-click inline editors, the meeting
 * cell carries the project as a sub-line, and the filter popover previews its
 * result count on the Apply button before you commit.
 *
 * ⚠️ CONTENT POLICY: every value below is invented. The owner's reference
 * screenshots carry real data — a real account, a client's phone number, a live
 * project code, and Arabic action items from an actual meeting. None of that may
 * appear on a public marketing page, so only the SHAPE was taken. The rows
 * deliberately reuse the same account, meetings and people as tabs 1-3 so the
 * five tabs read as one workspace rather than five screenshots — including in
 * Arabic, where the summaries are worded identically to HomeView's and
 * MinutesView's rather than translated a second time.
 *
 * ⚠️ AR COPY IS A PROPOSAL PENDING OWNER SIGN-OFF (style guide workflow, step 7).
 */

/* The CSS tone key and the sort key, NOT the label. ActionsView looks the
   visible word up per locale; leaving the English string doing both jobs is how
   the Arabic rows would have lost their priority colours. */
export type Priority = 'High' | 'Medium' | 'Low';

export interface DemoRow {
  id: number;
  summary: string;
  /** The app's second line, shown under the summary and editable only in the modal. */
  details?: string;
  meeting: string;
  /** Omitted where the meeting has none — the app hides the sub-line entirely. */
  project?: string;
  /**
   * Whether the scripted project filter keeps this row. A FLAG, not a string
   * compare against the displayed project name: the count the Apply button
   * previews has to be the same number in both locales, and deriving it from
   * display text makes it a translation away from disagreeing with the list.
   */
  inFilter?: boolean;
  /** '' renders as the app's em-dash placeholder. */
  owner: string;
  priority: Priority;
  /** Display text, already formatted. '' renders as '—'. */
  deadline: string;
  /* Urgency is stored, not derived from a clock. The app computes it against
     today's date; a mockup that did the same would quietly go stale — every row
     "overdue" a month after launch — and there is no real today here to compute
     against. Storing it keeps the demo saying what we meant it to say. */
  overdue?: boolean;
  soon?: boolean;
}

/** The project the scripted filter selects. Four of the nine rows carry it. */
export const FILTER_PROJECT: Record<Locale, string> = {
  en: 'Northgate Logistics',
  ar: 'نورثجيت اللوجستية',
};

/* Sorted the way the app sorts by default: deadline ascending, empty deadlines
   always last, ties broken by priority descending. Completed items would also
   sink to the bottom — see COMPLETED_ROWS below for why the demo does not
   re-sort when the script ticks one.

   ONLY ROWS 3 AND 4 CARRY A `details` LINE (owner, 2026-09-06: "reduce the
   amount of text ... so this doesn't read as a wall of text"). Four of nine had
   one, which stopped reading as "some items carry a note" and started reading
   as prose. Two is enough to show the field exists.

   Which two is not arbitrary, and the constraints pick them almost uniquely:

   - Row 4 is fixed. It is the row the compact layout keeps expanded, so it is
     the only details line a visitor at 480 or below ever sees. Drop it and the
     field vanishes from the narrow band entirely.
   - The second one must sit OUTSIDE the scripted project filter, so that the
     filtered state the sequence rests on shows one details line in four rows
     rather than two.
   - It must also not be one of COMPLETED_ROWS. A description under a
     struck-through title is the densest thing on the row, and it lands at the
     exact moment the demo wants the eye on the counters instead.

   Rows 1 and 2 are the completed pair and row 4 is spoken for, which leaves
   row 3.

   THE AR SET IS THE SAME NINE ROWS, id for id: same ids, same priorities, same
   deadlines, same owners, same in-filter flags. Only the words change. That is
   what lets the script, the KPI arithmetic and the filter preview stay one
   implementation rather than two that can drift. */
const EN_ROWS: readonly DemoRow[] = [
  {
    id: 1,
    summary: 'Send the cleaning contractor a revised quote',
    meeting: 'Warehouse fit-out sync',
    project: FILTER_PROJECT.en,
    inFilter: true,
    owner: 'Reem A.',
    priority: 'High',
    deadline: '08 Sep',
    overdue: true,
  },
  {
    id: 2,
    summary: 'Submit the Phase 2 quotation before the deadline',
    meeting: 'Quotation review',
    project: 'Coastal Walkway',
    owner: 'Sara T.',
    priority: 'High',
    deadline: '09 Sep',
    overdue: true,
  },
  {
    id: 3,
    summary: 'Follow up on outstanding scope questions',
    details: 'Scope is still underdefined, which is holding up pricing.',
    meeting: 'National Day Activation',
    owner: 'Khalid M.',
    priority: 'High',
    deadline: '10 Sep',
    soon: true,
  },
  {
    id: 4,
    summary: 'Order cleaning and sanitizing supplies for the new warehouse',
    /* This is the row the compact layout keeps expanded, so it is the one whose
       details line a visitor at ≤480 actually reads. */
    details: 'Enough for the first month, then review against usage.',
    meeting: 'Warehouse fit-out sync',
    project: FILTER_PROJECT.en,
    inFilter: true,
    /* Left unassigned so the scripted edit has something real to do. Unowned
       items are the common case in the owner's own reference screenshots. */
    owner: '',
    priority: 'Medium',
    deadline: '12 Sep',
    soon: true,
  },
  {
    id: 5,
    summary: 'Price the agreed craft and decor elements',
    meeting: 'National Day Activation',
    owner: 'Sara T.',
    priority: 'High',
    deadline: '15 Sep',
    soon: true,
  },
  {
    id: 6,
    summary: 'Identify a decor designer for the setup',
    meeting: 'National Day Activation',
    owner: 'Reem A.',
    priority: 'Medium',
    deadline: '15 Sep',
    soon: true,
  },
  {
    id: 7,
    summary: 'Draft the warehouse readiness plan',
    meeting: 'Warehouse fit-out sync',
    project: FILTER_PROJECT.en,
    inFilter: true,
    owner: 'Reem A.',
    priority: 'Medium',
    deadline: '18 Sep',
  },
  {
    id: 8,
    summary: 'Confirm the racking installation date',
    meeting: 'Warehouse fit-out sync',
    project: FILTER_PROJECT.en,
    inFilter: true,
    owner: 'Omar S.',
    priority: 'Low',
    deadline: '22 Sep',
  },
  {
    id: 9,
    summary: 'Recheck the boardwalk material costs',
    meeting: 'Quotation review',
    project: 'Coastal Walkway',
    owner: 'Khalid M.',
    priority: 'Low',
    deadline: '',
  },
];

/* مصدر throughout, per §3: nine repeated imperatives down a task list is the
   exact case the rule names. The deadlines keep the EN set's leading zero so the
   column still aligns, and the digits stay Western (§7). */
const AR_ROWS: readonly DemoRow[] = [
  {
    id: 1,
    summary: 'إرسال عرض سعر محدث لمقاول النظافة',
    meeting: 'اجتماع تجهيز المستودع',
    project: FILTER_PROJECT.ar,
    inFilter: true,
    owner: 'ريم الحربي',
    priority: 'High',
    deadline: '08 سبتمبر',
    overdue: true,
  },
  {
    id: 2,
    summary: 'تقديم عرض سعر المرحلة 2 قبل الموعد النهائي',
    meeting: 'مراجعة عرض السعر',
    project: 'ممشى الواجهة البحرية',
    owner: 'سارة التميمي',
    priority: 'High',
    deadline: '09 سبتمبر',
    overdue: true,
  },
  {
    id: 3,
    summary: 'متابعة الأسئلة المعلقة حول النطاق',
    details: 'لا يزال النطاق غير محدد بدقة، ما يؤخر التسعير.',
    meeting: 'فعالية اليوم الوطني',
    owner: 'خالد المطيري',
    priority: 'High',
    deadline: '10 سبتمبر',
    soon: true,
  },
  {
    id: 4,
    /* «الجديد» dropped from the EN's "for the new warehouse". Measured, not
       guessed: with it the cell overran by 2px at 1440 and lost its last word to
       an ellipsis for the sake of two pixels. It also fails §0's بلاغة test — the
       project is a warehouse fit-out, so "new" is carried by the context and the
       word is حشو. HomeView says this line identically; change both together. */
    summary: 'طلب مواد النظافة والتعقيم للمستودع',
    details: 'كمية تكفي الشهر الأول، ثم تُراجع على أساس الاستهلاك.',
    meeting: 'اجتماع تجهيز المستودع',
    project: FILTER_PROJECT.ar,
    inFilter: true,
    owner: '',
    priority: 'Medium',
    deadline: '12 سبتمبر',
    soon: true,
  },
  {
    id: 5,
    summary: 'تسعير عناصر الحرف والديكور المتفق عليها',
    meeting: 'فعالية اليوم الوطني',
    owner: 'سارة التميمي',
    priority: 'High',
    deadline: '15 سبتمبر',
    soon: true,
  },
  {
    id: 6,
    summary: 'تحديد مصمم ديكور للتنفيذ',
    meeting: 'فعالية اليوم الوطني',
    owner: 'ريم الحربي',
    priority: 'Medium',
    deadline: '15 سبتمبر',
    soon: true,
  },
  {
    id: 7,
    summary: 'إعداد خطة جاهزية المستودع',
    meeting: 'اجتماع تجهيز المستودع',
    project: FILTER_PROJECT.ar,
    inFilter: true,
    owner: 'ريم الحربي',
    priority: 'Medium',
    deadline: '18 سبتمبر',
  },
  {
    id: 8,
    summary: 'تأكيد موعد تركيب الرفوف',
    meeting: 'اجتماع تجهيز المستودع',
    project: FILTER_PROJECT.ar,
    inFilter: true,
    owner: 'عمر الشمري',
    priority: 'Low',
    deadline: '22 سبتمبر',
  },
  {
    id: 9,
    summary: 'مراجعة تكاليف مواد الممشى',
    meeting: 'مراجعة عرض السعر',
    project: 'ممشى الواجهة البحرية',
    owner: 'خالد المطيري',
    priority: 'Low',
    deadline: '',
  },
];

export const ROWS: Record<Locale, readonly DemoRow[]> = { en: EN_ROWS, ar: AR_ROWS };

/**
 * The two rows the script ticks, and the one whose owner it fills in.
 *
 * Both completions are the OVERDUE rows, which is what makes the counters worth
 * watching: Overdue empties to zero rather than just decrementing.
 *
 * DELIBERATE DIVERGENCE (owner, 2026-09-06): in the app a completed item jumps
 * to the bottom of the list immediately (sortActions keeps an incomplete-first
 * invariant under every column and direction). Here the row stays where it is,
 * dimmed and struck through. Two rows flying downwards mid-sequence in a 640px
 * frame reads as the list breaking, not as the list sorting.
 */
const COMPLETED_ROWS = [1, 2] as const;
export const OWNER_ROW_ID = 4;
/* Both locales carry the same nine ids in the same order, so one index serves
   both — see the note on AR_ROWS. */
export const OWNER_ROW_INDEX = EN_ROWS.findIndex((r) => r.id === OWNER_ROW_ID);
export const OWNER_EDIT_VALUE: Record<Locale, string> = {
  en: 'Omar S.',
  ar: 'عمر الشمري',
};

/** Base KPI counts. Pending + completed = total, and the header count agrees. */
const BASE = { next7: 6, pending: 24, overdue: 2, completed: 31 } as const;
export const TOTAL_ITEMS = BASE.pending + BASE.completed;
export const FILTERED_COUNT = EN_ROWS.filter((r) => r.inFilter).length;

export interface KpiCounts {
  next7: number;
  pending: number;
  overdue: number;
  completed: number;
}

/* Both scripted completions are overdue items, so ticking one moves three of the
   four cards and leaves Next 7 days alone — which is correct, and is also the
   detail that makes the counters read as derived rather than decorative. */
export function countsFor(done: number): KpiCounts {
  return {
    next7: BASE.next7,
    pending: BASE.pending - done,
    overdue: BASE.overdue - done,
    completed: BASE.completed + done,
  };
}

/** Which control the dot is travelling towards. Named per TARGET, not per phase. */
export type Aim = 'check-a' | 'check-b' | 'owner' | 'filter' | 'project' | 'apply';

/** How far through the filter flow we are. The panel is a popover ≥480 and a
    bottom sheet below it — same states, two surfaces, as in the app. */
export type FilterPhase = 'idle' | 'open' | 'picked' | 'applied';

/**
 * Both layouts render at once and a container query hides one, so a target has
 * two candidate elements. Resolution picks whichever is actually laid out —
 * `offsetParent` is the cheap "is it on screen" test — which keeps the choice on
 * the same container query that decides the layout instead of a second copy of
 * that threshold in JS. Same idiom as the recorder's four record surfaces.
 */
export type Surface = 'wide' | 'compact';

export interface ActionsDemo {
  /** Ids ticked so far, in script order. */
  done: readonly number[];
  counts: KpiCounts;
  /** True while the owner cell is showing its inline text field. */
  editingOwner: boolean;
  /** True once the value has been entered INTO the open field, before commit. */
  ownerTyped: boolean;
  /**
   * True once the edit commits; before that the cell shows the placeholder.
   *
   * A FLAG, not the string it commits. The hook drives a sequence and has no
   * locale — holding the display text here would have pinned the committed
   * owner to one language while every other cell in the row translated. The
   * view resolves OWNER_EDIT_VALUE for its own shell.
   */
  ownerCommitted: boolean;
  filter: FilterPhase;
  aim: Aim | null;
  /**
   * What the dot keys its measurement off. `aim` alone is not enough: opening
   * the Project dim unfolds its option list, and at ≤480 the filter surface is a
   * bottom sheet anchored to the frame's floor — so the sheet grows UPWARD and
   * every control in it moves, including the one the dot is already sitting on.
   * Folding `filter` in re-measures on exactly those transitions and no others.
   *
   * A derived key rather than "re-measure on every render", for the reason
   * TouchDot documents: a press usually swaps the thing underneath it, and a dot
   * that chased every commit would leave the control at the instant the press is
   * meant to read as having changed it.
   */
  aimKey: string | null;
  pressing: boolean;
  dotVisible: boolean;
  /** Reduced motion: the sequence never runs, we open on the finished state. */
  still: boolean;
  register: (aim: Aim, surface: Surface) => (el: HTMLElement | null) => void;
  resolve: () => HTMLElement | null;
}

/* The whole script, in ms from mount, against tab 4's 15s dwell. Every number is
   a start time, not a duration. A press is 250ms of contraction and its effect
   lands as the press ENDS, so the change always reads as caused by the touch.

   SLOWED 2026-09-06 (owner: "too fast, user might get overwhelmed"). The first
   cut ran six presses between 1.25s and 6.30s — one every 0.84s — inside a 9s
   dwell. Two numbers govern the pacing, and the first was the real defect:

     AIM -> PRESS must exceed the dot's 780ms travel (Recorder.css, .pnrec__touch
     transition), or the press fires while the finger is still moving. It was
     350ms, so every press landed at ~45% of the journey. It is 850ms now: the
     dot arrives, settles, then presses.

     PRESS -> NEXT AIM is what a reader gets to see the RESULT in. 550ms, up
     from ~200ms.

   Those two put the last press at 11.30s, so the dwell went to 15s to keep a
   ~3.2s hold on the filtered list — a visitor who looks up mid-cycle should
   find the result rather than a half-open panel. Change either gap and move
   TAB_CYCLE_OVERRIDE in Hero.tsx with it. */
const T = {
  appear: 900,
  aimCheckA: 900,
  pressCheckA: 1750,
  aimCheckB: 2550,
  pressCheckB: 3400,
  aimOwner: 4200,
  pressOwner: 5050,
  /** The value lands in the open field. Without this beat the field opens empty
      and commits empty-to-filled in one frame, which reads as a box that
      flickered rather than as a value being entered — the cell we edit is an
      UNASSIGNED one, so there is no prior text to stand in for the typing. */
  typeOwner: 5750,
  /** Commits on its own — a scripted Enter needs no second press. */
  commitOwner: 6350,
  aimFilter: 6900,
  pressFilter: 7750,
  aimProject: 8550,
  pressProject: 9400,
  aimApply: 10200,
  pressApply: 11050,
  hideDot: 11750,
} as const;

const PRESS_MS = 250;

export function useActionsDemo(): ActionsDemo {
  /* Read once, at mount, rather than subscribing: the sequence is scheduled in
     one shot below and a mid-flight preference change has nothing to re-drive.
     Guarded for SSR — this hook runs inside the prerender. */
  const still = useRef(
    typeof window !== 'undefined' &&
      Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  ).current;

  const [done, setDone] = useState<readonly number[]>(still ? COMPLETED_ROWS : []);
  const [editingOwner, setEditingOwner] = useState(false);
  const [ownerTyped, setOwnerTyped] = useState(false);
  const [ownerCommitted, setOwnerCommitted] = useState(still);
  const [filter, setFilter] = useState<FilterPhase>(still ? 'applied' : 'idle');
  const [aim, setAim] = useState<Aim | null>(null);
  const [pressing, setPressing] = useState(false);
  const [dotVisible, setDotVisible] = useState(false);

  /* One registry rather than a ref per target: there are six aims across two
     surfaces, and twelve named refs threaded through two components is a lot of
     wiring for something a Map does in one line. */
  const els = useRef(new Map<string, HTMLElement>());
  const setters = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const aimRef = useRef<Aim | null>(null);
  aimRef.current = aim;

  const register = useMemo(
    () => (a: Aim, surface: Surface) => {
      const key = `${a}:${surface}`;
      /* Cached so each render hands React the SAME callback — a fresh one every
         render would make it detach and re-attach the ref on every commit. */
      let fn = setters.current.get(key);
      if (!fn) {
        fn = (el: HTMLElement | null) => {
          if (el) els.current.set(key, el);
          else els.current.delete(key);
        };
        setters.current.set(key, fn);
      }
      return fn;
    },
    []
  );

  const resolve = useMemo(
    () => () => {
      const a = aimRef.current;
      if (!a) return null;
      const vis = (s: Surface) => {
        const el = els.current.get(`${a}:${s}`);
        return el && el.offsetParent !== null ? el : null;
      };
      return vis('wide') ?? vis('compact');
    },
    []
  );

  useEffect(() => {
    if (still) return;

    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };
    /* Every press goes through here so the contraction and the change it causes
       cannot drift apart: the effect is scheduled off the press's own end. */
    const press = (ms: number, effect: () => void) => {
      at(ms, () => setPressing(true));
      at(ms + PRESS_MS, () => {
        setPressing(false);
        effect();
      });
    };

    at(T.appear, () => setDotVisible(true));
    at(T.aimCheckA, () => setAim('check-a'));
    press(T.pressCheckA, () => setDone([COMPLETED_ROWS[0]]));
    at(T.aimCheckB, () => setAim('check-b'));
    press(T.pressCheckB, () => setDone([COMPLETED_ROWS[0], COMPLETED_ROWS[1]]));

    at(T.aimOwner, () => setAim('owner'));
    press(T.pressOwner, () => setEditingOwner(true));
    at(T.typeOwner, () => setOwnerTyped(true));
    at(T.commitOwner, () => {
      setEditingOwner(false);
      setOwnerTyped(false);
      setOwnerCommitted(true);
    });

    at(T.aimFilter, () => setAim('filter'));
    press(T.pressFilter, () => setFilter('open'));
    at(T.aimProject, () => setAim('project'));
    press(T.pressProject, () => setFilter('picked'));
    at(T.aimApply, () => setAim('apply'));
    press(T.pressApply, () => setFilter('applied'));

    at(T.hideDot, () => {
      setDotVisible(false);
      setAim(null);
    });

    return () => timers.forEach(window.clearTimeout);
  }, [still]);

  return useMemo(
    () => ({
      done,
      counts: countsFor(done.length),
      editingOwner,
      ownerTyped,
      ownerCommitted,
      filter,
      aim,
      aimKey: aim && `${aim}:${filter}`,
      pressing,
      dotVisible,
      still,
      register,
      resolve,
    }),
    [done, editingOwner, ownerTyped, ownerCommitted, filter, aim, pressing, dotVisible, still, register, resolve]
  );
}

/* Context for the same reason the recorder has one: the sequence drives several
   surfaces at once — the table, the mobile card list, the KPI strip, the filter
   panel and the dot — and they are one page state, not five. */
const ActionsDemoContext = createContext<ActionsDemo | null>(null);

export const ActionsDemoProvider = ActionsDemoContext.Provider;

export function useActionsDemoState(): ActionsDemo | null {
  return useContext(ActionsDemoContext);
}
